/**
 * navigation/rootNavigator.tsx
 * Top-level gate. SK Upsell owns NO auth — the platform owns login / signup /
 * profile. This gate only answers "is there a valid, onboarded session?":
 *
 *   • consume a verification link (?mode=verify&token=…) if present
 *   • probe GET /v1/me:
 *       200 + profile complete   → app shell
 *       200 + profile INCOMPLETE → bounce to platform to finish onboarding
 *       401 / error              → bounce to centralized platform auth
 *
 * Sign-out clears the session and hands back to the platform auth.
 */
import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Platform } from 'react-native';

import { LightColors as C } from '../theme/theme';
import { getCurrentUser, logoutUser } from '../services/auth/auth';
import { isOffline } from '../services/client';
import { setDemoMode } from '../services/_demo/demoMode';
import Spinner from '../components/spinner';
import AppShell from './appShell';
import { redirectToNotInPlan, redirectToPlatformAuth } from './platformAuthRedirect';
import { isNotInPlan } from './planGate';

type Phase = 'booting' | 'app';

interface MeResponse {
  /** subscribed_products: the product websites the org bought; null = no
   *  restriction. Read it through navigation/planGate, never directly. */
  data?: { user?: { profile_complete?: boolean }; subscribed_products?: string[] | null };
  user?: { profile_complete?: boolean };
  subscribed_products?: string[] | null;
}

export default function RootNavigator() {
  const [phase, setPhase] = useState<Phase>('booting');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Read-only identity probe. SK Upsell owns no auth — everything else is
        // SK-Platform-FE's. 200 → in; 401 → bounce to the platform login.
        const me = (await getCurrentUser()) as MeResponse;
        if (!active) return;
        const user = me?.data?.user ?? me?.user;
        if (!user) {
          redirectToPlatformAuth();
          return;
        }
        if (user.profile_complete === false) {
          redirectToPlatformAuth();
          return;
        }
        // Signed in, but this org's plan does not include the Upsell website.
        // The session stays — their own console still calls Upsell. No list = open.
        if (isNotInPlan((me?.data ?? me)?.subscribed_products) && redirectToNotInPlan()) {
          return;
        }
        setPhase('app');
      } catch (e) {
        if (!active) return;
        // Platform unreachable at all (local dev / offline) → seeded demo so the
        // whole app stays coherent. A real 401/403 (has a response) still hands
        // off to the centralized platform auth.
        if (isOffline(e)) {
          setDemoMode(true);
          setPhase('app');
        } else {
          redirectToPlatformAuth();
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await logoutUser();
    } finally {
      redirectToPlatformAuth();
    }
  };

  if (phase === 'app') return <AppShell onSignOut={handleSignOut} />;

  return (
    <View style={styles.boot}>
      <Spinner size={28} />
    </View>
  );
}

const styles = StyleSheet.create({
  boot: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: C.paper,
    ...(Platform.OS === 'web' ? { height: '100vh' as unknown as number } : {}),
  },
});
